import React from "react";
import paisavalaLogo from "../../../../../assets/Image/paisavala.png";
import { numberToWords } from "amount-to-words";
import useFormattedDate from "../../../../../context/useFormattingUtils";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import useInvoicePrint from "./templates/useInvoicePrint";
import PrintableInvoice from "./templates/PrintableInvoice";
import { normalizeInvoiceForPrint } from "./templates/normalizeInvoiceForPrint";
import { getDefaultInvoiceLayout } from "./templates/invoiceLayoutDefaults";
import { useConfig } from "../../../../../context/ConfigContext";

const QuickInvoicemodal = ({ open, onClose, data, invoiceType = "pos" }) => {
  const { formatDate } = useFormattedDate();
  const { formatCurrency } = useFormattingUtils();
  const { printInvoice } = useInvoicePrint();
  const { invoiceLayoutSettings, storeInfo } = useConfig() || {};
  const layout = (invoiceLayoutSettings && invoiceLayoutSettings[invoiceType]) || getDefaultInvoiceLayout(invoiceType);

  if (!open) return null;

  const sales_invoice = data?.sales_invoice || data?.invoice || {};
  const sales_invoice_products = Array.isArray(data?.sales_invoice_products)
    ? data.sales_invoice_products
    : Array.isArray(data?.products)
      ? data.products
      : [];

  const safeValue = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  const safeNumber = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? 0 : number;
  };

  const safeCurrency = (value) => {
    const number = safeNumber(value);
    return formatCurrency ? formatCurrency(number) : number.toFixed(2);
  };

  const payableAmount =
    safeNumber(sales_invoice?.payable_amount) ||
    safeNumber(sales_invoice?.final_after_discount) ||
    safeNumber(sales_invoice?.total_amount);

  const formattedPayableAmount = safeCurrency(payableAmount);
  const currencySymbol = String(formattedPayableAmount)
    .replace(/[\d.,\s]/g, "")
    .trim();

  const formatAmountInWords = (amount, symbol) => {
    const integerPart = Math.floor(amount);
    const fractionalPart = Math.round((amount - integerPart) * 100);

    let amountInWords = "";

    if (symbol === "₹") {
      amountInWords = `Rupees ${numberToWords(integerPart)}`;
      if (fractionalPart > 0) {
        amountInWords += ` and ${numberToWords(fractionalPart)} Paise`;
      }
    } else if (symbol === "$") {
      amountInWords = `${numberToWords(integerPart)} Dollars`;
      if (fractionalPart > 0) {
        amountInWords += ` and ${numberToWords(fractionalPart)} Cents`;
      }
    } else if (symbol === "£") {
      amountInWords = `${numberToWords(integerPart)} Pounds`;
      if (fractionalPart > 0) {
        amountInWords += ` and ${numberToWords(fractionalPart)} Pence`;
      }
    } else {
      amountInWords = `${numberToWords(integerPart)}`;
      if (fractionalPart > 0) {
        amountInWords += ` and ${numberToWords(fractionalPart)}`;
      }
    }

    return `${amountInWords} Only`;
  };

  const handlePrint = () => {
    // Routed through the shared settings-driven template system instead of
    // relying on this modal's own DOM as the print source.
    printInvoice(invoiceType, data);
  };

  const totalSoldQtyStock = sales_invoice_products.reduce(
    (total, product) => total + safeNumber(product?.sold_qty_stock || product?.qty),
    0
  );

  const totalRate = sales_invoice_products.reduce(
    (total, product) => total + safeNumber(product?.selling_price || product?.rate),
    0
  );

  const totalAmount = sales_invoice_products.reduce(
    (total, product) =>
      total +
      safeNumber(
        product?.total_amount ||
          product?.amount ||
          product?.before_discount ||
          0
      ),
    0
  );

  const totalAfterDiscount = sales_invoice_products.reduce(
    (total, product) =>
      total + safeNumber(product?.after_discount || product?.total || 0),
    0
  );

  const additionalDiscount = safeNumber(sales_invoice.additional_discount_value);
  const roundOff = safeNumber(sales_invoice.round_off);

  const brandColor = "#282E26";
  const secondaryColor = "#4A5546";
  const accentColor = "#FFAF32";

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.62)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1100,
    padding: "24px",
  };

  const modalStyle = {
    background: "#f8fafc",
    borderRadius: "22px",
    width: "94%",
    maxWidth: "1120px",
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.35)",
    position: "relative",
  };

  const actionBarStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 5,
    borderTopLeftRadius: "22px",
    borderTopRightRadius: "22px",
  };

  const invoicePaperStyle = {
    background: "#ffffff",
    margin: "22px auto",
    width: "96%",
    maxWidth: "980px",
    borderRadius: "22px",
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
  };

  const sectionStyle = {
    padding: "24px 28px",
  };

  const labelStyle = {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "5px",
  };

  const valueStyle = {
    fontSize: "14px",
    color: "#0f172a",
    fontWeight: 800,
    wordBreak: "break-word",
  };

  const infoBoxStyle = {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "13px 14px",
  };

  const tableHeaderStyle = {
    background: brandColor,
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "12px",
    padding: "11px 10px",
    textAlign: "center",
    border: "1px solid #1f2937",
    whiteSpace: "nowrap",
  };

  const tableCellStyle = {
    padding: "11px 10px",
    fontSize: "12px",
    color: "#111827",
    border: "1px solid #e5e7eb",
    textAlign: "center",
    verticalAlign: "top",
  };

  const summaryRowStyle = {
    padding: "9px 12px",
    fontSize: "13px",
    color: "#475569",
    fontWeight: 700,
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
  };

  const renderInfoBox = (label, value) => (
    <div style={infoBoxStyle}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{safeValue(value)}</div>
    </div>
  );

  // NOTE: printing is now handled entirely by useInvoicePrint()/PrintableInvoice
  // (a hidden, settings-driven template mounted off-screen). This modal is only
  // the on-screen preview, so it must NOT declare its own @media print rules —
  // an old `body * { visibility: hidden }` override here would hide the new
  // template's printable content and silently fall back to this hardcoded look.
  return (
    <>
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={actionBarStyle} className="retail-invoice-no-print">
            <div>
              <div style={{ fontSize: "18px", fontWeight: 900, color: "#111827" }}>
                Retail Invoice Preview
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                Review retail invoice before printing.
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <button
                type="button"
                onClick={handlePrint}
                style={{
                  background: brandColor,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "999px",
                  padding: "10px 18px",
                  fontSize: "13px",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Print Invoice
              </button>

              <button
                type="button"
                onClick={onClose}
                style={{
                  background: "#ffffff",
                  color: "#111827",
                  border: "1px solid #d1d5db",
                  borderRadius: "999px",
                  width: "38px",
                  height: "38px",
                  fontSize: "18px",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          </div>

          <div id="retail-invoice-print-area" className="retail-invoice-modal-paper" style={{ background: "#ffffff" }}>
            <PrintableInvoice layout={layout} invoice={normalizeInvoiceForPrint(data, invoiceType)} storeInfo={storeInfo || {}} />
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickInvoicemodal;
